import { useCallback, useState } from "react";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

declare global {
  interface Window {
    gtranslateSettings?: Record<string, unknown>;
  }
}

/**
 * GTranslate widget that loads ONLY when user explicitly clicks the translate button.
 * This eliminates the 5.4s JavaScript execution time from initial page load.
 * 
 * Strategy: Show a placeholder button until clicked, then load the real widget.
 */
export function GTranslateWidget() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  const loadGTranslate = useCallback(() => {
    if (isLoaded || isLoading) return;
    
    setIsLoading(true);
    setError(false);

    // Configure GTranslate settings before loading script
    window.gtranslateSettings = {
      default_language: "es",
      native_language_names: true,
      detect_browser_language: true,
      wrapper_selector: ".gtranslate_wrapper",
      alt_flags: { en: "usa" },
    };

    // Create and load the script
    const script = document.createElement("script");
    script.src = "https://cdn.gtranslate.net/widgets/latest/popup.js";
    script.id = "gtranslate-popup-js";
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
      setIsLoading(false);
    };

    script.onerror = () => {
      setError(true);
      setIsLoading(false);
      console.warn("GTranslate failed to load");
    };

    document.head.appendChild(script);
  }, [isLoaded, isLoading]);

  // Once loaded, the GTranslate widget will render into .gtranslate_wrapper
  // We just need to provide that container
  if (isLoaded) {
    return (
      <div 
        className="gtranslate_wrapper fixed bottom-4 left-4 z-[9999]" 
        style={{ contain: 'layout style', minWidth: '40px', minHeight: '40px' }}
      />
    );
  }

  // Show placeholder button until clicked
  return (
    <div className="fixed bottom-4 left-4 z-[9999]">
      <Button
        variant="outline"
        size="icon"
        onClick={loadGTranslate}
        disabled={isLoading}
        className="w-10 h-10 rounded-full bg-white shadow-lg border-gray-200 hover:bg-gray-50 disabled:opacity-50"
        aria-label="Traducir página"
        title="Traducir página"
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        ) : error ? (
          <Globe className="w-4 h-4 text-destructive" />
        ) : (
          <Globe className="w-4 h-4 text-muted-foreground" />
        )}
      </Button>
    </div>
  );
}
